import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import {
  getInvestmentsFundsOperable,
  getOperablesSymbolsData,
} from "../../../../connectors/marketApi";
import { PATH_APP } from "../../../../routes/paths";
import { useAppSelector } from "../../../../store/hooks";
import { useNonOperableFunds } from "../../../../pages/MarketPage/ui/AccordionTable/ui/Funds/useNonOperableFunds";

import { AutocompleteItem } from "@nextui-org/react";
import Autocomplete from "../../../Autocomplete/Autocomplete";
import Skeleton from "../../../Skeleton/Skeleton";

import SearchIcon from "../../../../assets/images/icons/search-icon.svg?react";

import { SymbolStaticData } from "../../../../types/types";

import styles from "./InstrumentSelector.module.scss";
import { navigateToInstrumentDetail } from "../../../../pages/MarketPage/ui/AccordionTable/ui/Stocks";
import { Currency, Instrument } from "../../../../pages/MarketPage/types";

interface HistoryItem {
  company: string;
  isin: string;
  symbol: string;
  type: string;
  currency: string;
  fund_id: number;
}

const InstrumentSelector = () => {
  const {
    searchInstrumentContainer,
    autocompleteItemContainer,
    itemContainer,
    itemNameContainer,
    itemTypeOfInstrumentContainer,
    clearButton,
    autocompleteWrapper,
    selectorSkeleton,
  } = styles;

  const { accountSelected } = useAppSelector((state) => state.userAccounts);

  const [searchValue, setSearchValue] = useState("");
  const [key, setKey] = useState(0);
  const [historyItems, setHistoryItems] = useState<HistoryItem[]>([]);

  const navigate = useNavigate();

  const { pathname } = useLocation();

  const istrumentsDicionary = {
    STOCKS: "Acciones",
    CEDEARS: "CEDEARs",
    SOVEREIGN_BONDS: "Bonos",
    TREASURY_NOTES: "Letras",
    CORPORATE_BONDS: "Corporativos",
    OPTIONS: "Opciones",
    REPO_OPERATION: "Cauciones",
    FUNDS: "Fondos",
  };

  const currencyDictionary = {
    DOLLAR_MEP: "MEP",
    DOLLAR_CCL: "CCL",
    ARS: "ARS",
  };

  const { data: symbolsData } = useQuery({
    queryFn: () => getOperablesSymbolsData(),
    queryKey: ["symbols-static-data"],
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });

  const { data: investmentsFunds } = useQuery({
    queryFn: () => getInvestmentsFundsOperable(),
    queryKey: ["investments-funds"],
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: !!accountSelected,
    staleTime: 5 * 60 * 1000,
    select: (response) => {
      return response
        ?.filter((fund) => {
          if (fund.name.startsWith("Allaria Sustentable")) {
            return false;
          }

          if (Math.floor(Number(accountSelected?.number) / 1_000_000) === 24) {
            return fund.name.includes("Clase Blanqueo");
          }

          if (accountSelected?.isJuridical) {
            return (
              (fund.name.includes("Clase B") &&
                !fund.name.includes("Clase Blanqueo")) ||
              fund.name.includes("Clase C")
            );
          }

          return fund.name.includes("Clase A");
        })
        ?.map((fund) => {
          const text = fund.name;
          const parts = text.split(" - ");
          let fundName = parts[0].trim();

          if (!fundName.includes("Clase")) {
            const classMatch = text.match(/(Clase [A-Za-z]+)/i);
            if (classMatch) {
              fundName = `${fundName} - ${classMatch[0]}`;
            }
          }

          return {
            fund_id: fund.fund_id,
            symbol: fundName,
            company: `${
              currencyDictionary[
                fund.currency as "DOLLAR_MEP" | "DOLLAR_CCL" | "ARS"
              ]
            }`,
            type: "FUNDS",
            currency: fund.currency,
          };
        });
    },
  });

  const { data: rawFundsData } = useQuery({
    queryFn: () => getInvestmentsFundsOperable(),
    queryKey: ["investments-funds"],
    enabled: !!accountSelected,
    staleTime: Infinity,
  });

  const { nonOperableFunds } = useNonOperableFunds({
    accountSelected: accountSelected ?? null,
    operableFunds: rawFundsData ?? [],
  });

  const nonOperableFundsForSelector = useMemo(
    () =>
      nonOperableFunds.map((fund) => {
        const parts = fund.name.split(" - ");
        let fundName = parts[0].trim();
        const classMatch = fund.name.match(/(Clase [A-Za-z]+)/i);
        if (classMatch && !fundName.includes("Clase")) {
          fundName = `${fundName} - ${classMatch[0]}`;
        }
        return {
          fund_id: fund.fund_id,
          symbol: fundName,
          company: `${currencyDictionary[fund.currency as "DOLLAR_MEP" | "DOLLAR_CCL" | "ARS"]}`,
          type: "FUNDS",
          currency: fund.currency,
        };
      }),
    [nonOperableFunds],
  );

  const data =
    symbolsData && investmentsFunds
      ? [...symbolsData, ...investmentsFunds, ...nonOperableFundsForSelector]
      : [];

  const filteredItems =
    searchValue.trim() && !!data
      ? (() => {
          const query = searchValue.toLowerCase();

          const getSymbolPriority = (symbol: string) => {
            const s = symbol.toLowerCase();
            if (s === query) return 0;
            if (s.startsWith(query)) {
              const nextChar = s[query.length];
              if (nextChar && /[0-9]/.test(nextChar)) return 2;
              return 1;
            }
            return 3;
          };

          const bySymbol = data
            .filter((item) => item.symbol.toLowerCase().includes(query))
            .sort((a, b) => {
              const pa = getSymbolPriority(a.symbol);
              const pb = getSymbolPriority(b.symbol);
              if (pa !== pb) return pa - pb;
              return a.symbol.length - b.symbol.length;
            });

          const byCompany = data.filter(
            (item) =>
              !item.symbol.toLowerCase().includes(query) &&
              item.company?.toLowerCase().includes(query),
          );

          return [...bySymbol, ...byCompany].slice(0, 10);
        })()
      : [];

  useEffect(() => {
    if (
      pathname != PATH_APP.instrument &&
      pathname != PATH_APP.searchInstruments
    ) {
      setSearchValue("");
      setKey((prev) => prev + 1);
    }
  }, [pathname]);

  const handleClear = () => {
    setSearchValue("");
    setKey((prev) => prev + 1);
  };

  const HISTORY_KEY = "INSTUMENT_HISTORY";
  const currentHistoryKey = accountSelected?.number
    ? `${HISTORY_KEY}_${accountSelected.number}`
    : HISTORY_KEY;

  const HISTORY_TODAY = new Date().toISOString().split("T")[0];

  const saveToHistory = (item: HistoryItem) => {
    const newHistory = [
      item,
      ...historyItems.filter(
        (h) => h.symbol !== item.symbol || h.type !== item.type,
      ),
    ].slice(0, 5);
    setHistoryItems(newHistory);
    localStorage.setItem(
      currentHistoryKey,
      JSON.stringify({ items: newHistory, date: HISTORY_TODAY }),
    );
  };

  const onNavigate = (selectedItem: HistoryItem) => {
    navigateToInstrumentDetail({
      navigate,
      symbol: selectedItem.symbol,
      instrument: selectedItem.type as Instrument | "FUNDS",
      settledPeriod: "T1",
      currency: selectedItem.currency as Currency,
      fundId:
        selectedItem.type === "FUNDS" ? selectedItem?.fund_id.toString() : "",
    });
    setSearchValue("");
    setKey((prev) => prev + 1);
  };

  useEffect(() => {
    const savedHistory = localStorage.getItem(currentHistoryKey);
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        if (parsed.date === HISTORY_TODAY && Array.isArray(parsed.items)) {
          setHistoryItems(parsed.items);
        } else {
          localStorage.removeItem(currentHistoryKey);
          setHistoryItems([]);
        }
      } catch (e) {
        localStorage.removeItem(currentHistoryKey);
        setHistoryItems([]);
      }
    } else {
      setHistoryItems([]);
    }
  }, [currentHistoryKey, HISTORY_TODAY]);

  return (
    <div className={searchInstrumentContainer}>
      {data?.length > 0 ? (
        <div className={autocompleteWrapper}>
          <Autocomplete
            key={key}
            allowsEmptyCollection={false}
            allowsCustomValue={false}
            isClearable={false}
            aria-label="Buscar instrumentos"
            items={searchValue.trim() ? filteredItems : historyItems}
            inputValue={searchValue}
            onValueChange={(val) => setSearchValue(val)}
            menuTrigger="focus"
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                searchValue.trim() &&
                filteredItems.length > 0
              ) {
                const firstItem = filteredItems[0];
                saveToHistory(firstItem as HistoryItem);
                onNavigate(firstItem as HistoryItem);
              }
            }}
            onSelectionChange={(selectValue) => {
              if (!selectValue) return;

              const selectedItemValue = selectValue as string;

              const selectedItem = data.find(
                (item) =>
                  `${item.symbol} - ${item.company}`.toLowerCase() ===
                    selectedItemValue.toLowerCase() ||
                  item.symbol.toLowerCase() === selectedItemValue.toLowerCase(),
              );

              if (!selectedItem) return;

              saveToHistory(selectedItem as HistoryItem);
              onNavigate(selectedItem as HistoryItem);
            }}
            placeholder="Buscar instrumentos"
            selectorIcon={null}
            startContent={<SearchIcon />}
            variant="bordered"
          >
            {(item) => {
              const currentItem = item as SymbolStaticData & {
                currency?: string;
                fund_id?: string;
              };

              return (
                <AutocompleteItem
                  key={`${currentItem.symbol} - ${currentItem.company}`}
                  textValue={currentItem.symbol}
                  selectedIcon={null}
                  className={autocompleteItemContainer}
                  hideSelectedIcon
                >
                  <div className={itemContainer}>
                    <div className={itemNameContainer}>
                      <h2>{currentItem.symbol}</h2>
                      <p>{currentItem?.company}</p>
                    </div>
                    <div className={itemTypeOfInstrumentContainer}>
                      {istrumentsDicionary[currentItem.type] ?? ""}
                    </div>
                  </div>
                </AutocompleteItem>
              );
            }}
          </Autocomplete>
          {searchValue.trim() && (
            <button onClick={handleClear} className={clearButton} type="button">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1rem"
                height="1rem"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M9.06095 7.99935L12.0297 5.0306C12.1706 4.88995 12.2498 4.69909 12.25 4.50001C12.2502 4.30093 12.1713 4.10993 12.0306 3.96904C11.89 3.82814 11.6991 3.74889 11.5 3.74871C11.301 3.74853 11.11 3.82745 10.9691 3.9681L8.00032 6.93685L5.03157 3.9681C4.89068 3.8272 4.69958 3.74805 4.50032 3.74805C4.30107 3.74805 4.10997 3.8272 3.96907 3.9681C3.82818 4.10899 3.74902 4.30009 3.74902 4.49935C3.74902 4.69861 3.82818 4.8897 3.96907 5.0306L6.93782 7.99935L3.96907 10.9681C3.82818 11.109 3.74902 11.3001 3.74902 11.4993C3.74902 11.6986 3.82818 11.8897 3.96907 12.0306C4.10997 12.1715 4.30107 12.2506 4.50032 12.2506C4.69958 12.2506 4.89068 12.1715 5.03157 12.0306L8.00032 9.06185L10.9691 12.0306C11.11 12.1715 11.3011 12.2506 11.5003 12.2506C11.6996 12.2506 11.8907 12.1715 12.0316 12.0306C12.1725 11.8897 12.2516 11.6986 12.2516 11.4993C12.2516 11.3001 12.1725 11.109 12.0316 10.9681L9.06095 7.99935Z"
                  fill="#003366"
                />
              </svg>
            </button>
          )}
        </div>
      ) : (
        <Skeleton className={selectorSkeleton} />
      )}
    </div>
  );
};

export default InstrumentSelector;
